import React, { Component, Fragment } from 'react';
import { findDOMNode } from 'react-dom';
import intl from 'react-intl-universal';
import { isMobile, isTablet } from 'react-device-detect';
import PropTypes from 'prop-types';
import styled from 'src/ui/library/styled';
import classNames from 'classnames';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ExpandLess from '@material-ui/icons/ExpandLess';
import FixedSelectWrapper from './FixedSelectWrapper';

const ITEM_HEIGHT = 48;

let styles = (theme, props) => {
  const isBigLabel = false;
  const isSmall = window.innerWidth < 640;
  return {
    root: {
      position: 'relative',
      width: '100%',
      maxWidth: '300px',
      display: 'inline-block',
      border: 'solid 1px transparent',
      backgroundColor: theme.colors.eyGrey4,
    },
    enableDropdownField: {
      borderBottom: '1px solid #2E2E38',
      "&:hover, &:focus-within":{
        background: '#E1E1E6',
      }
    },
    labelPlaceholder: {
      height: isBigLabel ? '24px' : '16px',
    },
    floatLabel: {
      position: 'absolute',
      top: 'calc(50% - 8px)',
      width: 'calc(100% - 32px)', // leave room for the chevron so text does not overlap
      left: '8px',
      transition: 'transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, font-size 0ms',
      transformOrigin: 'top left',
      color: theme.colors.eyConfidentGrey,
      fontSize: '1rem',
      //   fontFamily: 'EYInterstate',
      ...(props.useEllipsisLabel && {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      }),
    },
    mobileShrinkLabel: {
      top: '15px',
    },
    floatLabelShrink: {
      transform: `translate(0, -${isBigLabel ? 16 : 8}px)`,
      fontSize: '0.8em',
      color: theme.colors.eyGrey1,
      ...(props.useEllipsisLabel && {}),
    },
    focused: {
      // border: `1px solid ${theme.colors.eyGrey6}`,
    },
    hasError: {
      // border: `solid 1px ${theme.colors.red}`,
      borderBottom: `solid 1px ${theme.colors.red}`,
    },
    disabled: {
      color: '#aa9fa2',
    },
    labelAsterisk: {
      color: 'red',
    },
    '@global': {
      '.Select-loading-zone': {
        display: 'none', // this is causing some UI issues. hiding for now
      },
      '.Select-control': {
        height: '100%',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        background: 'transparent',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      '.Select-multi-value-wrapper': {
        flexGrow: 1,
        display: 'flex',
        flexWrap: 'wrap',
        overflow: 'hidden',
        justifyContent: 'start',
      },
      '.justifyCenter .Select-multi-value-wrapper': {
        justifyContent: 'center',
      },
      '.Select--multi .Select-input': {
        margin: 0,
      },
      '.Select--multi .Select-value': {
        margin: '5px 5px 0px 0px',
      },
      '.Select.has-value.is-clearable.Select--single > .Select-control .Select-value': {
        padding: '0px 0px 0px 8px !important',
      },
      '.Select-noresults': {
        padding: theme.spacing.unit * 2,
      },
      '.Select-input': {
        display: 'inline-flex !important',
        // marginLeft: '0px !important',
        marginLeft: props.align === 'C' ? '10px' : '0px', // IE 11 center align fix
        padding: 0,
        width: '0 !important',
      },
      '.Select-input input': {
        background: 'transparent',
        border: 0,
        padding: 0,
        cursor: 'default',
        display: 'inline-block',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        margin: 0,
        outline: 0,
        // width:"5px !important", //IE 11 center align fix
      },
      '.Select-placeholder': {
        display: 'none',
      },
      '.Select-menu-outer': {
        backgroundColor: theme.palette.background.paper,
        borderBottomRightRadius: '0px',
        borderBottomLeftRadius: '0px',
        boxShadow: 'none',
        border: `1px solid ${theme.colors.eyGrey6}`,
        position: 'absolute',
        left: '-1px',
        width: 'calc(100% + 2px)',
        zIndex: 1000,
        maxHeight: ITEM_HEIGHT * 4.5,
      },
      '.Select-menu-outer-mobile': {
        backgroundColor: theme.palette.background.paper,
        borderBottomRightRadius: '0px',
        borderBottomLeftRadius: '0px',
        boxShadow: 'none',
        border: `1px solid ${theme.colors.eyGrey6}`,
        position: 'absolute',
        width: 'calc(100% + 2px)',
        zIndex: 1000,
        maxHeight: ITEM_HEIGHT * 4.5,
      },
      '.Select.is-focused:not(.is-open) > .Select-control': {
        boxShadow: 'none',
        // background: theme.colors.eyGrey4,
        background: '#E1E1E6 !important',
      },
      '.Select.is-focused > .Select-control': {
        // background: theme.colors.eyGrey4,
        background: '#E1E1E6 !important',
      },
      '.Select-option.is-focused': {
        backgroundColor: theme.colors.eyGrey4,
      },
      '.Select.is-disabled .Select-control': {
        backgroundColor: '#f6f5fa',
      },
      '.Select-menu': {
        maxHeight: ITEM_HEIGHT * 4.5 - 2 /* 2 pixels for border */,
        overflowY: 'scroll',
      },
      '.Select-menu div': {
        boxSizing: 'content-box',
        height: 'auto',
        overflow: 'hidden',
        fontSize: '14px',
        '&:hover': {
          backgroundColor: theme.colors.eyGrey4,
        },
      },
      '.autocomplete-item-name':{
        maxWidth: '96%',
      },
      '.Select-clear-zone': {
        display: 'none !important',
      },
      '.Select-arrow-zone': {
        top: '50%',
        display: 'inline-block',
        position: 'relative',
        right: '2px',
        // transform: 'translateY(calc(-50% - 5px))',
        // '-ms-transform': 'translateY(-50%) translateY(-5px)',
        '-webkit-transform': 'none',
        transform: 'none',
      },
      '.Select-value': {
        background: 'none !important',
        border: 'none !important',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: isSmall ? 'calc(100% - 25px)' : 'calc(100% - 30px)', // 75px accounts for the padding + the chevron => changed to 30Px for IE 11 center align fix
        display: 'block',
        marginLeft: props.align === 'C' ? '10px' : '0px', // IE 11 center align fix
      },
      '.Select-value-icon': {
        display: 'none !important',
      },
      '.Select-value-label': {
        padding: '2px 0px 2px 0px !important',
      },
      //padding removed and inline padding added to child div in dropdown class
      '.Select-option':{
        padding:'0px',
      },
    },
  };
};

class DropdownBase extends Component {
  static propTypes = {
    /** Classname to be applied to Dropdown */
    className: PropTypes.string,
    /** Callback fired when Change event is called */
    onChange: PropTypes.func.isRequired,
    /** If `true`, Dropdown will be Disabled */
    isDisabled: PropTypes.bool,
    /** If `true`, Dropdown will show loader till the data is loaded */
    isLoading: PropTypes.bool,
    /** If `true`, Dropdown will show cross icon to delete the value*/
    isClearable: PropTypes.bool,
    /** If `true`, Dropdown will autoSearch for the records value*/
    isSearchable: PropTypes.bool,
    /** It will show label for the dropdown*/
    label: PropTypes.string.isRequired,
    /** Function to override default option rendering */
    renderOption: PropTypes.func,
    /** Function to override default value rendering */
    renderValue: PropTypes.func,
    /** Function to override default filtering */
    onFilter: PropTypes.func,
    /** Default value to seed uncontrolled component */
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Function to override default arrowRenderer */
    renderIcon: PropTypes.func,
    /** If `true`, Dropdown border to be shown in red color */
    hasError: PropTypes.bool,
  };

  static defaultProps = {
    renderOption: null,
    renderValue: null,
    onFilter: null,
    onChange: null,
    renderIcon: null,
    className: '',
    useEllipsisLabel: false,
    hasError: false,
  };

  constructor(props) {
    super(props);
    let modifiedState = {};

    if (props.value) {
      modifiedState = { value: props.value, isFocusd: true };
    }

    this.state = {
      isFocused: false,
      searchText: null,
      value: null,
      ...modifiedState,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({ value: this.props.value });
    }
  }

  arrowRenderer = props => {
    const { isOpen } = props;
    this.hideScrollBar(isOpen);
    let color = this.props.isDisabled ? 'disabled' : 'black';
    if (isOpen) return <ExpandLess color={color} />;
    else return <ExpandMore color={color} />;
  };

  blurSelectInput = () => {
    // unfortunately `react-select` does not provide a way to access this ref
    // so we need to query for it
    const inputRef = findDOMNode(this.selectRef.input).querySelector('input');
    if (inputRef && typeof inputRef === 'function') {
      inputRef.blur();
    }
  };

  onChange = selectedValue => {
    if (selectedValue !== undefined) {
      this.setState({ value: selectedValue, isFocused: selectedValue === null ? false : true }, () => {
        if (this.props.onChange) {
          const retValue = selectedValue === null ? null : this.state.value.value;
          if (retValue === null) {
            this.blurSelectInput();
          }

          this.props.onChange({
            target: {
              name: this.props.name || null,
              id: this.props.id || null,
              ...selectedValue,
              value: retValue,
            },
          });
        }
      });
    }
    if (selectedValue === null) this.setState({ value: null });
  };

  onFilter = () => {
    if (this.props.onFilter) return this.props.onFilter;

    return (row, val) => {
      return row.label
        .toString()
        .toLowerCase()
        .indexOf(val.toLowerCase()) === 0
        ? true
        : false;
    };
  };

  hideScrollBar = isFocused => {
    const scrollDiv = document.createElement('div');
    scrollDiv.style.width = '99px';
    scrollDiv.style.height = '99px';
    scrollDiv.style.position = 'absolute';
    scrollDiv.style.top = '-9999px';
    scrollDiv.style.overflow = 'scroll';

    document.body.appendChild(scrollDiv);
    const scrollbarSize = scrollDiv.offsetWidth - scrollDiv.clientWidth;
    document.body.removeChild(scrollDiv);

    let viewPortEl = document.querySelector('.htcombo-popup-wrapper'); // for dropdown inside HT Combo Table
    if (viewPortEl === null) {
      viewPortEl = document.querySelector('body'); // for dropdown in body
      if (viewPortEl === null) {
        return null;
      }
    }

    const isOverflowing = viewPortEl.scrollHeight > viewPortEl.clientHeight;

    if (isFocused) {
      viewPortEl.style.overflow = 'hidden';
      if (isOverflowing) {
        viewPortEl.style.paddingRight = scrollbarSize + 'px';
      }
    } else {
      viewPortEl.style.overflow = 'auto';
      if (isOverflowing) {
        viewPortEl.style.paddingRight = '0px';
      }
    }
  };

  toggleFocus = (isFocused, event) => {
    event.persist();
    //this.hideScrollBar(val);

    this.setState(
      {
        isFocused,
      },
      () => {
        if (isFocused && typeof this.props.onFocus === 'function') {
          this.props.onFocus(event);
        }

        if (!isFocused && typeof this.props.onBlur === 'function') {
          this.props.onBlur(event);
        }
      },
    );
  };

  handleKeyDown = event => {
    switch (event.keyCode) {
      case 8:
      case 46:
        //prevent the first option to get auto-selected on pressing backspace and delete
        if (!this.state.searchText) {
          event.preventDefault();
        }
        break;
      case 27:
        // unfortunately `react-select` does not provide a way to access this ref
        // so we need to query for it
        this.blurSelectInput();
        this.setState({ isFocused: false });
        break;
      default:
        break;
    }
  };

  handleInputChange = searchText => {
    this.setState({ searchText });
  };

  handleLabelClick = () => {
    this.selectRef.focus();
  };

  render() {
    const {
      className,
      isDisabled,
      isLoading,
      isClearable,
      isSearchable,
      label,
      onBlur,
      onFocus,
      onChange,
      onFilter,
      styleType,
      renderOption,
      renderValue,
      classes,
      defaultValue,
      hideIconOnSelect,
      renderIcon,
      hasError,
      align,
      ...otherProps
    } = this.props;

    const { value, isFocused, searchText } = this.state;
    const rootClass = classNames(
      {
        [classes.root]: true,
        [classes.focused]: isFocused,
        [classes.hasError]: this.props.hasError,
        [classes.enableDropdownField]: !this.props.isDisabled,
      },
      className,
    );

    return (
      <Fragment>
        <div className={rootClass}>
          <div className={classes.labelPlaceholder} />
          <FixedSelectWrapper
            ref={el => (this.selectRef = el)}
            value={value}
            onChange={this.onChange}
            disabled={isDisabled}
            isLoading={isLoading}
            onInputKeyDown={this.handleKeyDown}
            onInputChange={this.handleInputChange}
            clearable={isClearable}
            searchable={isSearchable}
            onBlur={evt => this.toggleFocus(false, evt)}
            onFocus={evt => this.toggleFocus(true, evt)}
            optionRenderer={data => renderOption(data, searchText)}
            valueComponent={renderValue}
            arrowRenderer={this.arrowRenderer}
            filterOption={this.onFilter()}
            ignoreAccents={false}
            noResultsText={intl.get('autocomplete.noResults')}
            {...otherProps}
          />
          <label
            onClick={this.handleLabelClick}
            className={classNames({
              [classes.floatLabel]: true,
              [classes.disabled]: isDisabled,
              [classes.floatLabelShrink]: isFocused || value !== null ? true : false,
              [classes.mobileShrinkLabel]: (isMobile || isTablet) && (isFocused || value === null || value === ''),
            })}>
            {label}
            {this.props.required && 
            intl.get('questionnaire.requiredLabel')
            // <span className={classes.labelAsterisk}> *</span>
            }
          </label>
        </div>
      </Fragment>
    );
  }
}
export { DropdownBase };
export default styled(DropdownBase)(styles);
